import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchRosterManagementReportAction,
  setLoading,
} from "../slices/rosterManagementReportSlice";
import { FetchRosterManagementReportParams } from "../api/fetchRosterManagementReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface RosterManagementReportFilters {
  searchTerm?: string;
  companyIds?: number[];
  eventIds?: number[];
  sessionIds?: number[];
  sessionDateFrom?: string;
  sessionDateTo?: string;
}

function buildFilterSortParams(
  filters: RosterManagementReportFilters,
  sorting: SortingState,
): Omit<FetchRosterManagementReportParams, "page" | "pageSize"> {
  const params: Omit<FetchRosterManagementReportParams, "page" | "pageSize"> =
    {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.companyIds?.length) params.companyIds = filters.companyIds;
  if (filters.eventIds?.length) params.eventIds = filters.eventIds;
  if (filters.sessionIds?.length) params.sessionIds = filters.sessionIds;
  if (filters.sessionDateFrom) params.sessionDateFrom = filters.sessionDateFrom;
  if (filters.sessionDateTo) params.sessionDateTo = filters.sessionDateTo;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

export function useRosterManagementReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, loading, error } = useSelector(
    (state: RootState) => state.rosterManagementReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  // Seed the session filter from the URL so the Sessions report drill-down
  // (`?sessionIds=<id>&sessionLabel=<name>`) lands on a roster already scoped
  // to that session, and surfaces the session's name in the indicator.
  const [searchParams] = useSearchParams();
  const initialFilters: RosterManagementReportFilters = (() => {
    const sessionIds = searchParams
      .getAll("sessionIds")
      .map(Number)
      .filter((id) => !Number.isNaN(id));
    return sessionIds.length ? { sessionIds } : {};
  })();
  const sessionLabel = searchParams.get("sessionLabel") ?? undefined;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] =
    useState<RosterManagementReportFilters>(initialFilters);

  useEffect(() => {
    dispatch(
      fetchRosterManagementReportAction({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...buildFilterSortParams(filters, sorting),
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof RosterManagementReportFilters,
      value: string | number[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    sessionLabel,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
